import {
  getItemTemplate,
  type ItemInstance,
} from "@vinyl/shared";
import { el } from "./dom.js";
import { bindItemTooltip, hideItemTooltip } from "./item-tooltip.js";
import { setupWindow, type WindowChromeHandle } from "./window-chrome.js";

export interface LootWindowCallbacks {
  onTakeOne: (bagId: string, instanceId: string) => void;
  onTakeAll: (bagId: string) => void;
  onClose: () => void;
}

export class LootWindow {
  readonly root: HTMLElement;
  private readonly chrome: WindowChromeHandle;
  private bagId: string | null = null;
  private items: ItemInstance[] = [];
  private listEl: HTMLElement;
  private takeAllBtn: HTMLButtonElement;

  constructor(private readonly cb: LootWindowCallbacks) {
    this.listEl = el("div", { class: "loot-list" });
    this.takeAllBtn = el("button", { class: "btn", type: "button" }, "Take all") as HTMLButtonElement;
    this.takeAllBtn.addEventListener("click", () => {
      if (this.bagId) cb.onTakeAll(this.bagId);
    });
    this.root = el("aside", { class: "loot-window hidden" });
    this.chrome = setupWindow({
      windowEl: this.root,
      title: "Loot",
      storageKey: "loot",
      onClose: () => this.onClosed(),
    });
    this.root.append(
      this.chrome.header,
      this.listEl,
      el("div", { class: "loot-footer" }, this.takeAllBtn),
    );
  }

  open(bagId: string, items: ItemInstance[]): void {
    this.bagId = bagId;
    this.items = items;
    this.chrome.open();
    this.render();
  }

  setContents(bagId: string, items: ItemInstance[]): void {
    if (!this.chrome.isOpen()) return;
    if (this.bagId !== bagId) return;
    this.items = items;
    this.render();
  }

  closeIfBag(bagId: string): void {
    if (this.bagId === bagId) this.close();
  }

  close(): void { this.chrome.close(); }

  private onClosed(): void {
    this.bagId = null;
    this.items = [];
    hideItemTooltip();
    this.cb.onClose();
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    this.listEl.replaceChildren();
    if (this.items.length === 0) {
      this.listEl.append(el("div", { class: "loot-empty" }, "Empty."));
      this.takeAllBtn.disabled = true;
      return;
    }
    this.takeAllBtn.disabled = false;
    for (const item of this.items) {
      const template = getItemTemplate(item.templateId);
      if (!template) continue;
      const isRare = item.affixes.length > 0;
      const row = el("div", { class: `loot-row${isRare ? " loot-row-rare" : ""}` });
      const label = el("div", { class: "loot-label" });
      const namePart = el("span", { class: "loot-name" }, template.name);
      label.append(namePart);
      if (isRare) label.append(el("span", { class: "loot-rare-tag" }, "[Rare]"));
      if (template.stackable && item.stackCount > 1) {
        label.append(el("span", { class: "loot-stack" }, `×${item.stackCount}`));
      }
      bindItemTooltip(row, template, item);
      const takeBtn = el(
        "button",
        { class: "btn-ghost loot-take", type: "button" },
        "Take",
      ) as HTMLButtonElement;
      takeBtn.addEventListener("click", () => {
        if (this.bagId) this.cb.onTakeOne(this.bagId, item.instanceId);
      });
      row.append(label, takeBtn);
      this.listEl.append(row);
    }
  }
}

